import { useState, type FormEvent } from 'react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../../i18n';
import { useUpsertEvalConfig } from '../../../hooks/useAdminRemunerationConfig';
import { SectionHeader } from '../SectionHeader';
import { FieldLabel } from './fields';
import { INPUT_MEDIUM } from './scale';

export function EvalConfigSection({
  evalConfig,
  onError,
  onClear,
  onSuccess,
}: {
  evalConfig: { passThreshold: number; maxScore: number; criteria: { label: string }[] } | null;
  onError: (err: unknown) => void;
  onClear: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertEvalConfig();
  const [threshold, setThreshold] = useState(() => String(evalConfig?.passThreshold ?? 3));
  const [maxScore, setMaxScore] = useState(() => String(evalConfig?.maxScore ?? 5));
  const [criteria, setCriteria] = useState(() =>
    (evalConfig?.criteria ?? []).map((c) => c.label).join('\n'),
  );

  const pass = Number(threshold);
  const max = Number(maxScore);
  const numbersOk = Number.isFinite(pass) && Number.isFinite(max);
  const thresholdAboveMax = numbersOk && pass > max;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!Number.isFinite(pass) || pass < 0 || !Number.isFinite(max) || max < 1) return;
    onClear();
    upsert.mutate({ passThreshold: pass, maxScore: max, criteria }, { onError, onSuccess });
  }

  return (
    <div className="flex flex-col gap-3">
      <SectionHeader
        title={t('gest:admin.remuneration.config.eval.title')}
        description={t('gest:admin.remuneration.config.eval.description')}
      />
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="ad2-eval-threshold">{t('gest:admin.remuneration.config.eval.threshold')}</FieldLabel>
            <input
              id="ad2-eval-threshold"
              type="number"
              min={0}
              max={1000}
              step="0.5"
              required
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className={INPUT_MEDIUM}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="ad2-eval-max">{t('gest:admin.remuneration.config.eval.maxScore')}</FieldLabel>
            <input
              id="ad2-eval-max"
              type="number"
              min={1}
              max={1000}
              step="0.5"
              required
              value={maxScore}
              onChange={(e) => setMaxScore(e.target.value)}
              className={INPUT_MEDIUM}
            />
          </div>
        </div>
        {numbersOk ? (
          <p className="text-chip text-slate-500 dark:text-slate-400">
            {t('gest:admin.remuneration.config.eval.thresholdHelp', {
              threshold,
              max: maxScore,
            })}
          </p>
        ) : null}
        {thresholdAboveMax ? (
          <p className="text-chip text-amber-700 dark:text-amber-300">
            {t('gest:admin.remuneration.config.eval.thresholdWarn')}
          </p>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="ad2-eval-criteria">{t('gest:admin.remuneration.config.eval.criteria')}</FieldLabel>
          <textarea
            id="ad2-eval-criteria"
            rows={4}
            maxLength={2000}
            value={criteria}
            onChange={(e) => setCriteria(e.target.value)}
            placeholder={t('gest:admin.remuneration.config.eval.criteriaPlaceholder')}
            className={`${GS_COMPONENTS.input} h-auto`}
          />
          <p className="text-chip text-slate-500 dark:text-slate-400">
            {t('gest:admin.remuneration.config.eval.criteriaHint')}
          </p>
        </div>
        <div>
          <button type="submit" disabled={upsert.isPending} className={GS_COMPONENTS.btnAction}>
            {t('gest:admin.remuneration.config.eval.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
